/**
 * MAYAChain client for the shielded-memo swap flow.
 *
 * mayanode.mayachain.info serves cross-origin requests with permissive
 * CORS, so we hit it directly from the browser without going through
 * teleswap-bot.
 *
 * Surface:
 *   getZecInbound()          -> { halted, t_addr_vault }
 *   getZecMemoUA()           -> the u1… address shielded memos go to
 *   buildSwapMemo(...)       -> the "=:ASSET:..." memo string
 *   getSwapQuote(...)        -> { expected_amount_out, fees, ... }
 *   trackSwap(zec_txid)      -> mayanode `/mayachain/tx/:hash` status
 *
 * The ZcashWallet send flow constructs:
 *   recipient   = inbound.t_addr_vault          (transparent principal)
 *   memo_note   = { recipient_ua: getZecMemoUA(),
 *                   memo_text:    buildSwapMemo(...) }
 */

const MAYANODE_BASE = "https://mayanode.mayachain.info";

const TTL_MS = 60_000;

let inboundCache: { value: ZecInbound; fetchedAt: number } | null = null;
let memoUaCache: { value: string; fetchedAt: number } | null = null;

interface InboundAddressJson {
  chain: string;
  address: string;
  halted?: boolean;
  global_trading_paused?: boolean;
  shielded_memo_config?: {
    enabled?: boolean;
    unified_address?: string;
  };
}

export interface ZecInbound {
  /** MAYA's ZEC inbound vault address (transparent t1…). */
  t_addr_vault: string;
  /** True if the chain is halted — refuse to broadcast. */
  halted: boolean;
}

async function fetchInboundAddresses(): Promise<InboundAddressJson[]> {
  const r = await fetch(`${MAYANODE_BASE}/mayachain/inbound_addresses`);
  if (!r.ok) {
    throw new Error(`mayanode inbound_addresses HTTP ${r.status}`);
  }
  return (await r.json()) as InboundAddressJson[];
}

export async function getZecInbound(): Promise<ZecInbound> {
  if (inboundCache && Date.now() - inboundCache.fetchedAt < TTL_MS) {
    return inboundCache.value;
  }
  const list = await fetchInboundAddresses();
  const zec = list.find((e) => e.chain === "ZEC");
  if (!zec) throw new Error("mayanode reports no ZEC inbound");
  const value: ZecInbound = {
    t_addr_vault: zec.address,
    halted: !!(zec.halted || zec.global_trading_paused),
  };
  inboundCache = { value, fetchedAt: Date.now() };
  return value;
}

/**
 * MAYA's `shielded_memo_config.unified_address` — the UA that the
 * shielded-memo (0-value Sapling output) goes to. ONLY the memo lives
 * here; principal funds stay on the transparent vault from
 * getZecInbound. Sending value to this UA is a loss-of-funds bug.
 */
export async function getZecMemoUA(): Promise<string> {
  if (memoUaCache && Date.now() - memoUaCache.fetchedAt < TTL_MS) {
    return memoUaCache.value;
  }
  const list = await fetchInboundAddresses();
  const zec = list.find((e) => e.chain === "ZEC");
  if (!zec) throw new Error("mayanode reports no ZEC inbound");
  const cfg = zec.shielded_memo_config;
  if (!cfg?.enabled || !cfg.unified_address) {
    throw new Error(
      "MAYA shielded_memo_config is not enabled for ZEC — swap is unavailable right now",
    );
  }
  memoUaCache = { value: cfg.unified_address, fetchedAt: Date.now() };
  return cfg.unified_address;
}

export interface SwapMemoArgs {
  /** Destination asset, e.g. "ARB.USDC". */
  to_asset: string;
  /** Destination address on the to_asset's chain. */
  to_address: string;
  /** Optional minimum-out limit (in target-asset 1e8 / 1e6 units). */
  limit?: string | number;
  /** Affiliate handle. ZcashWallet routes through LeoFinance's broker. */
  affiliate?: string;
  /** Affiliate basis points. */
  affiliate_bps?: number;
  /** Streaming-swap params "interval/quantity/0". */
  streaming?: { interval: number; quantity: number };
}

/**
 * Compose the MAYAChain swap memo string. Format:
 *   =:ASSET:DEST:LIMIT:AFFILIATE:BPS
 * with optional streaming params "0/1/0" in place of LIMIT.
 *
 * 512-byte limit on the encrypted Sapling memo (the signer truncates
 * + Memo::from_bytes-validates per ZIP-302).
 */
export function buildSwapMemo(args: SwapMemoArgs): string {
  const limit = args.streaming
    ? `${args.streaming.interval}/${args.streaming.quantity}/0`
    : (args.limit ?? "0").toString();
  const affiliate = args.affiliate ?? "leo";
  const bps = args.affiliate_bps ?? 45;
  return `=:${args.to_asset}:${args.to_address}:${limit}:${affiliate}:${bps}`;
}

// ─── Quote ──────────────────────────────────────────────────────────────

export interface SwapQuoteInput {
  /** Amount in ZEC's atomic units (zats, 1e-8 ZEC). */
  amount_zats: number;
  to_asset: string;
  to_address: string;
  /** "leo" by default. */
  affiliate?: string;
  /** 45 by default (matches the existing LeoKit affiliate spec). */
  affiliate_bps?: number;
}

export interface SwapQuote {
  /** Expected destination-asset amount, in the destination's atomic units. */
  expected_amount_out: string;
  /** Estimated total seconds end-to-end (inbound + outbound). */
  total_swap_seconds: number;
  /** Fees in target-asset atomic units. */
  fees: {
    affiliate?: string;
    outbound?: string;
    liquidity?: string;
    total?: string;
  };
  /** MAYA's recommended memo for this swap (we still build our own). */
  memo: string | null;
  /** MAYA's `notes` — warnings + reminders we surface verbatim to the user. */
  notes: string | null;
  /** Raw response (for diagnostic copy in the UI). */
  raw: Record<string, unknown>;
}

interface QuoteJson {
  expected_amount_out?: string;
  total_swap_seconds?: number;
  fees?: SwapQuote["fees"];
  memo?: string;
  notes?: string;
  [k: string]: unknown;
}

export async function getSwapQuote(input: SwapQuoteInput): Promise<SwapQuote> {
  const params = new URLSearchParams({
    from_asset: "ZEC.ZEC",
    to_asset: input.to_asset,
    amount: String(input.amount_zats),
    destination: input.to_address,
    affiliate: input.affiliate ?? "leo",
    affiliate_bps: String(input.affiliate_bps ?? 45),
  });
  const url = `${MAYANODE_BASE}/mayachain/quote/swap?${params}`;
  const r = await fetch(url);
  if (!r.ok) {
    let body = "";
    try {
      body = await r.text();
    } catch {
      /* ignore */
    }
    throw new Error(`MAYA quote HTTP ${r.status}: ${body || "(no body)"}`);
  }
  const json = (await r.json()) as QuoteJson;
  return {
    expected_amount_out: json.expected_amount_out ?? "0",
    total_swap_seconds: json.total_swap_seconds ?? 0,
    fees: json.fees ?? {},
    memo: json.memo ?? null,
    notes: json.notes ?? null,
    raw: json as Record<string, unknown>,
  };
}

// ─── Status tracking ────────────────────────────────────────────────────

export interface SwapStatus {
  /** "observed" | "done" | "refunded" | "unknown" — best-effort */
  state: "observed" | "done" | "refunded" | "unknown";
  /** Hashes of outbound tx(es) once they exist. */
  out_hashes: string[];
}

interface MayanodeTxResponse {
  observed_tx?: {
    status?: string;
    out_hashes?: string[];
  };
}

export async function trackSwap(zecTxid: string): Promise<SwapStatus> {
  const r = await fetch(`${MAYANODE_BASE}/mayachain/tx/${zecTxid.toUpperCase()}`);
  if (r.status === 404) {
    return { state: "unknown", out_hashes: [] };
  }
  if (!r.ok) {
    throw new Error(`mayanode tx-status HTTP ${r.status}`);
  }
  const json = (await r.json()) as MayanodeTxResponse;
  const obs = json.observed_tx;
  const out_hashes = (obs?.out_hashes ?? []).filter(
    (h) => h && !/^0+$/.test(h),
  );
  let state: SwapStatus["state"];
  switch (obs?.status) {
    case "done":
      state = "done";
      break;
    case "refunded":
      state = "refunded";
      break;
    case "observed":
    case undefined:
      state = "observed";
      break;
    default:
      state = "unknown";
  }
  return { state, out_hashes };
}
